import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["disabled", "title"]
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "main-content" }
const _hoisted_4 = ["src"]
const _hoisted_5 = {
  key: 1,
  class: "card-emoji"
}
const _hoisted_6 = { class: "play-section" }
const _hoisted_7 = { class: "progress-indicator" }
const _hoisted_8 = { class: "progress-text" }
const _hoisted_9 = {
  class: "swipe-area",
  ref: "swipeArea"
}
const _hoisted_10 = { class: "settings-list" }
const _hoisted_11 = { class: "settings-item" }
const _hoisted_12 = { class: "switch" }
const _hoisted_13 = { class: "settings-item" }
const _hoisted_14 = { class: "switch" }

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWordsList } from '@/api/course-word'
import { getFavorites, addFavorites, deleteFavorites } from '@/api/favorites'
import { updateLearning } from '@/api/user'
import { getAccessToken } from '@/config/axios/auth'

const WORD_CONTENT_TYPE = 4


export default /*@__PURE__*/_defineComponent({
  __name: 'fullscreen',
  setup(__props) {

const isGuestUser = computed(() => getAccessToken() == null)
const isNonVipUser = computed(() =>
  !isGuestUser.value && localStorage.getItem('vipTag') === 'false'
)
const route = useRoute()
const router = useRouter()
const cardColors = [
  '#FF6B6B',
  '#4ECDC4',
  '#45B7D1',
  '#96CEB4',
  '#9B89B3',
  '#FFB6C1',
  '#8E44AD',
  '#F39C12',
  '#E91E63',
  '#00BCD4',
]

const cardBgColors = [
  'rgba(255, 107, 107, 0.15)',
  'rgba(78, 205, 196, 0.15)',
  'rgba(69, 183, 209, 0.15)',
  'rgba(150, 206, 180, 0.15)',
  'rgba(155, 137, 179, 0.15)',
  'rgba(255, 182, 193, 0.15)',
  'rgba(142, 68, 173, 0.15)',
  'rgba(243, 156, 18, 0.15)',
  'rgba(233, 30, 99, 0.15)',
  'rgba(0, 188, 212, 0.15)',
]

const emptyCard = {
  id: 0,
  en: '',
  zh: '',
  icon: '',
  color: '#FF6B6B',
  bgColor: 'rgba(255, 255, 255, 0.5)',
  coverUrl: '',
  audioUrl: '',
}

// 数据
const cards = ref([])

const currentIndex = ref(0)
const isCleanMode = ref(false)
const isPlaying = ref(false)
const isAutoMode = ref(false)
const autoPlayTimer = ref(null)
const showSettings = ref(false)
const showTranslation = ref(true)
const manualFlipAutoPlay = ref(true)
const favoriteCardIds = ref([])
const favoriteLoadingIds = ref([])
const audioPlayer = ref(null)
let learningUpdateTimer: ReturnType<typeof window.setInterval> | null = null
let isLearningUpdatePending = false

const stopLearningUpdateTimer = () => {
  if (learningUpdateTimer != null) {
    window.clearInterval(learningUpdateTimer)
    learningUpdateTimer = null
  }
}

const sendLearningUpdate = async () => {
  if (isGuestUser.value || isLearningUpdatePending) {
    return
  }

  isLearningUpdatePending = true
  try {
    await updateLearning()
  } catch {
    // Learning progress updates should not interrupt playback.
  } finally {
    isLearningUpdatePending = false
  }
}

const startLearningUpdateTimer = () => {
  stopLearningUpdateTimer()

  if (isGuestUser.value) {
    return
  }

  learningUpdateTimer = window.setInterval(sendLearningUpdate, 60 * 1000)
}

const openLoginPrompt = () => {
  router.replace('/login')
}

const ensureCanUseFullscreenContent = () => {
  if (isGuestUser.value) {
    router.replace('/login')
    return false
  }

  if (isNonVipUser.value) {
    router.replace('/vip')
    return false
  }

  return true
}

// 当前卡片
const currentCard = computed(() => cards.value[currentIndex.value] || emptyCard)

const getRouteSeriesId = () => {
  const rawSeriesId = Array.isArray(route.query.seriesId)
    ? route.query.seriesId[0]
    : route.query.seriesId
  const seriesId = Number(rawSeriesId)

  return Number.isFinite(seriesId) ? seriesId : undefined
}

const currentFavoriteContentId = computed(() => getRouteSeriesId())
const isCurrentCardFavorite = computed(() => favoriteCardIds.value.includes(currentFavoriteContentId.value))
const isCurrentFavoriteLoading = computed(() => favoriteLoadingIds.value.includes(currentFavoriteContentId.value))

const normalizeWordsResponse = (data) => {
  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.list)) {
    return data.list
  }

  return []
}

const getWordIcon = (english = '', index) => {
  const trimmedEnglish = String(english ?? '').trim()

  if (trimmedEnglish.length >= 2) {
    return trimmedEnglish.slice(0, 2)
  }

  if (trimmedEnglish.length === 1) {
    return `${trimmedEnglish}${trimmedEnglish.toLowerCase()}`
  }

  return String(index + 1)
}

const getList = async () => {
  const seriesId = getRouteSeriesId()

  stopPlay()
  currentIndex.value = 0

  if (!seriesId) {
    cards.value = []
    return
  }

  try {
    const data = await getWordsList(seriesId)
    cards.value = normalizeWordsResponse(data).map((word, index) => {
      const english = word.english || ''

      return {
        id: word.id ?? index + 1,
        en: english,
        zh: word.chinese || '',
        icon: getWordIcon(english, index),
        color: cardColors[index % cardColors.length],
        bgColor: cardBgColors[index % cardBgColors.length],
        coverUrl: word.coverUrl || '',
        audioUrl: word.audioUrl || '',
      }
    })
  } catch {
    cards.value = []
  }
}

const setFavoriteState = (cardId, isFavorite) => {
  const idSet = new Set(favoriteCardIds.value)

  if (isFavorite) {
    idSet.add(cardId)
  } else {
    idSet.delete(cardId)
  }

  favoriteCardIds.value = Array.from(idSet)
}

const loadFavoriteState = async (cardId) => {
  if(isGuestUser.value) {
    return
  }
  if (!cardId || favoriteLoadingIds.value.includes(cardId)) {
    return
  }

  favoriteLoadingIds.value = [...favoriteLoadingIds.value, cardId]
  try {
    const favorite = await getFavorites(WORD_CONTENT_TYPE, cardId)
    setFavoriteState(cardId, favorite != null)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== cardId)
  }
}

const toggleFavorite = async () => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }
  const contentId = currentFavoriteContentId.value
  if (!contentId || favoriteLoadingIds.value.includes(contentId)) {
    return
  }

  const wasFavorite = favoriteCardIds.value.includes(contentId)
  favoriteLoadingIds.value = [...favoriteLoadingIds.value, contentId]
  setFavoriteState(contentId, !wasFavorite)

  try {
    if (wasFavorite) {
      await deleteFavorites(WORD_CONTENT_TYPE, contentId)
    } else {
      await addFavorites({
        contentType: WORD_CONTENT_TYPE,
        contentId
      })
    }
  } catch {
    setFavoriteState(contentId, wasFavorite)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== contentId)
  }
}

// 发音
const speakWord = (text = currentCard.value.en) => {
  if (!ensureCanUseFullscreenContent()) {
    isPlaying.value = false
    return
  }

  const audioUrl = currentCard.value.audioUrl
  if (audioUrl) {
    stopCurrentAudio()
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }

    const audio = new Audio(audioUrl)
    audioPlayer.value = audio
    isPlaying.value = true
    audio.onended = () => {
      if (!isAutoMode.value) {
        isPlaying.value = false
      }
    }
    audio.onerror = () => {
      if (!isAutoMode.value) {
        isPlaying.value = false
      }
    }
    audio.play().catch(() => {
      if (!isAutoMode.value) {
        isPlaying.value = false
      }
    })
    return
  }

  if (!text) {
    isPlaying.value = false
    return
  }

  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
    isPlaying.value = true // 开始播放时设置状态
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = 0.85
    utterance.onend = () => {
      // 播放结束后重置播放状态
      if (!isAutoMode.value) {
        isPlaying.value = false
      }
    }
    speechSynthesis.speak(utterance)
  }
}

const stopCurrentAudio = () => {
  if (!audioPlayer.value) {
    return
  }

  audioPlayer.value.pause()
  audioPlayer.value.currentTime = 0
  audioPlayer.value = null
}

// 切换清屏模式
const toggleCleanMode = () => {
  isCleanMode.value = !isCleanMode.value
}

// 切换播放/暂停
const togglePlay = () => {
  if (isPlaying.value) {
    stopPlay()
  } else {
    startPlay()
  }
}

// 开始播放
const startPlay = () => {
  if (!ensureCanUseFullscreenContent()) {
    return
  }

  if (cards.value.length === 0) {
    return
  }

  isPlaying.value = true
  speakWord(currentCard.value.en)
  
  if (isAutoMode.value) {
    autoPlayTimer.value = setInterval(() => {
      nextCard(false)
    }, 3000)
  }
}

// 停止播放
const stopPlay = () => {
  isPlaying.value = false

  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
  }

  stopCurrentAudio()

  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

// 开始自动播放
const startAutoPlay = () => {
  if (cards.value.length === 0) {
    return
  }

  if (isAutoMode.value) {
    isAutoMode.value = false
    if (autoPlayTimer.value) {
      clearInterval(autoPlayTimer.value)
      autoPlayTimer.value = null
    }
    return
  }

  isAutoMode.value = true
  startPlay()
}



// 打开设置
const openSettings = () => {
  showSettings.value = true
}

const closeSettings = () => {
  showSettings.value = false
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/card')
}

// 上一张
const prevCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value - 1 + cards.value.length) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    speakWord(currentCard.value.en)
  }
  if (isAutoMode.value) {
    setTimeout(() => startPlay(), 100)
  }
}

// 下一张
const nextCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value + 1) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    speakWord(currentCard.value.en)
  }
  if (isAutoMode.value) {
    setTimeout(() => startPlay(), 100)
  }
}

// 触摸滑动
let touchStartX = 0
let touchEndX = 0

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  handleSwipe()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  const diff = touchStartX - touchEndX
  
  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0) {
      // 向左滑，下一张
      nextCard()
    } else {
      // 向右滑，上一张
      prevCard()
    }
  }
}

// 键盘事件
const handleKeydown = (e) => {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    prevCard()
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    togglePlay()
  } else if (e.key === 'Escape') {
    router.push('/category')
  }
}

watch(
  () => route.query.seriesId,
  async () => {
    await getList()
  }
)

watch(
  () => currentFavoriteContentId.value,
  (contentId) => {
    loadFavoriteState(contentId)
  },
  { immediate: true }
)

onMounted(async () => {
  startLearningUpdateTimer()
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })
  await getList()
  
  if (ensureCanUseFullscreenContent()) {
    setTimeout(() => {
      startPlay()
    }, 500)
  }
})

onUnmounted(() => {
  stopLearningUpdateTimer()
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchend', handleTouchEnd)
  stopPlay()
})

return (_ctx: any,_cache: any) => {
  const _component_desktop_brand_corner = _resolveComponent("desktop-brand-corner")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["fullscreen-page", { 'clean-mode': isCleanMode.value }]),
    onClick: toggleCleanMode
  }, [
    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "bg-gradient" }, null, -1)),
    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "orb orb-1" }, null, -1)),
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "orb orb-2" }, null, -1)),
    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "orb orb-3" }, null, -1)),
    _createVNode(_component_desktop_brand_corner),
    _createElementVNode("button", {
      type: "button",
      class: "back-btn",
      onClick: _withModifiers(handleGoBack, ["stop"]),
      "aria-label": "返回上一页"
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
    ]),
    _createElementVNode("header", {
      class: _normalizeClass(["page-header", { hidden: !showTranslation.value }])
    }, [
      _createElementVNode("h1", {
        class: _normalizeClass(["word-translation", { visible: true }])
      }, _toDisplayString(currentCard.value.zh), 1)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(["side-controls", { hidden: isCleanMode.value }])
    }, [
      _createElementVNode("button", {
        class: _normalizeClass(["control-btn favorite-btn", { active: isCurrentCardFavorite.value }]),
        disabled: isCurrentFavoriteLoading.value,
        onClick: _withModifiers(toggleFavorite, ["stop"]),
        title: isCurrentCardFavorite.value ? '取消收藏' : '收藏'
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "heart" })
      ], 10, _hoisted_1),
      _createElementVNode("button", {
        class: "control-btn",
        onClick: _withModifiers(openSettings, ["stop"]),
        title: "设置"
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "cog" })
      ]),
      _createElementVNode("button", {
        class: _normalizeClass(["control-btn auto-play-btn", { active: isAutoMode.value }]),
        onClick: _withModifiers(startAutoPlay, ["stop"]),
        title: isAutoMode.value ? '自动播放中' : '开始自动播放'
      }, [
        _createVNode(_component_font_awesome_icon, {
          icon: isAutoMode.value ? 'book-open' : ['fas', 'hand-pointer']
        }, null, 8, ["icon"])
      ], 10, _hoisted_2)
    ], 2),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        class: "image-container",
        onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (speakWord()), ["stop"]))
      }, [
        _createElementVNode("div", {
          class: "image-wrapper",
          style: _normalizeStyle({ background: currentCard.value.bgColor })
        }, [
          (currentCard.value.coverUrl)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: currentCard.value.coverUrl,
                alt: "",
                class: "card-cover"
              }, null, 8, _hoisted_4))
            : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(currentCard.value.icon), 1))
        ], 4)
      ]),
      _createElementVNode("h2", {
        class: _normalizeClass(["word-display", { visible: true }])
      }, _toDisplayString(currentCard.value.en), 1)
    ]),
    _createElementVNode("footer", {
      class: _normalizeClass(["bottom-controls", { hidden: isCleanMode.value }])
    }, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("button", {
          class: "play-btn",
          onClick: _withModifiers(togglePlay, ["stop"])
        }, [
          _createVNode(_component_font_awesome_icon, {
            icon: isPlaying.value ? 'pause' : 'play'
          }, null, 8, ["icon"])
        ])
      ])
    ], 2),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("span", _hoisted_8, _toDisplayString(cards.value.length === 0 ? 0 : currentIndex.value + 1) + "/" + _toDisplayString(cards.value.length), 1)
    ]),
    _createElementVNode("button", {
      class: "nav-btn prev",
      onClick: _withModifiers(prevCard, ["stop"])
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
    ]),
    _createElementVNode("button", {
      class: "nav-btn next",
      onClick: _withModifiers(nextCard, ["stop"])
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-right" })
    ]),
    _createElementVNode("div", _hoisted_9, null, 512),
    (showSettings.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "settings-modal",
          onClick: _withModifiers(closeSettings, ["stop"])
        }, [
          _createElementVNode("div", {
            class: "settings-content",
            onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "settings-text" }, [
                  _createElementVNode("p", { class: "settings-title" }, "显示翻译"),
                  _createElementVNode("p", { class: "settings-desc" }, "翻译显示在绘本顶部")
                ], -1)),
                _createElementVNode("label", _hoisted_12, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showTranslation).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, showTranslation.value]
                  ]),
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "slider" }, null, -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "settings-text" }, [
                  _createElementVNode("p", { class: "settings-title" }, "手动翻页时音频自动播放")
                ], -1)),
                _createElementVNode("label", _hoisted_14, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((manualFlipAutoPlay).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, manualFlipAutoPlay.value]
                  ]),
                  _cache[6] || (_cache[6] = _createElementVNode("span", { class: "slider" }, null, -1))
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})